import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Stack, Paper, Container, Typography, IconButton, Box, Divider, TextField, Modal, MenuItem, FormControl, InputLabel, Select, FormHelperText, FormControlLabel, Checkbox, Chip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import { INITIAL_LEAVE_FORM_STATE, LEAVE_TYPE_LABELS, LEAVE_TYPES, HALF_DAY_TYPE_LABELS, HALF_DAY_TYPES } from '../constants';
import LeaveRequestSchema from '../schema/leaveSchema';
import { getReviewers } from '../../../services/leaveService';

const SYSTEM_AUTO_LEAVE_REASON = 'SYSTEM_AUTO_APPROVED';

const LeaveRequestForm = ({ open, onClose, onSubmit, leaveData = null, isEdit = false }) => {
  // Fetch reviewers (managers and HR) for selection using the backend endpoint
  const { data: reviewersResponse } = useQuery({
    queryKey: ['reviewers'],
    queryFn: async () => {
      try {
        // Use the getReviewers endpoint which returns managers (roleId 2) and HR (roleId 6)
        const response = await getReviewers();
        return {
          reviewers: response?.data?.data || [],
          minStartDate: response?.data?.minStartDate || null,
        };
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        return { reviewers: [], minStartDate: null };
      }
    },
    enabled: open, // Only fetch when modal is open
  });

  const reviewersList = reviewersResponse?.reviewers || [];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      // Format dates to YYYY-MM-DD format for API
      const submitData = {
        ...values,
        startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
        // If half day is checked, force end date to start date
        endDate: values.isHalfDay && values.startDate 
          ? dayjs(values.startDate).format('YYYY-MM-DD') 
          : (values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null),
        // Only include halfDayType if isHalfDay is true
        halfDayType: values.isHalfDay ? values.halfDayType : null,
        // Include reviewers array (convert to array of IDs if needed)
        reviewers: values.reviewers && values.reviewers.length > 0 ? values.reviewers : undefined,
      };
      await onSubmit(submitData);
      // Only reset form on successful submission (not on error)
      if (!isEdit) {
        resetForm({ values: INITIAL_LEAVE_FORM_STATE });
      }
    } catch (error) {
      // Error is handled by mutation's onError, don't reset form on error
      // Form data will be preserved so user can fix and resubmit
    } finally {
      setSubmitting(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit && leaveData) {
      return {
        leaveType: leaveData.leaveType || '',
        startDate: leaveData.startDate ? dayjs(leaveData.startDate) : null,
        endDate: leaveData.endDate ? dayjs(leaveData.endDate) : null,
        isHalfDay: leaveData.isHalfDay || false,
        halfDayType: leaveData.halfDayType || '',
        reason: leaveData.reason === SYSTEM_AUTO_LEAVE_REASON ? 'System generated leave' : leaveData.reason || '',
        reviewers: leaveData.reviewers || [],
      };
    }
    return INITIAL_LEAVE_FORM_STATE;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: LeaveRequestSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const { values, errors, touched, isSubmitting, submitForm, getFieldProps, setFieldValue, resetForm } = formik;

  // Default UX: use backend-provided minimum start date.
  const startDateMin = reviewersResponse?.minStartDate
    ? dayjs(reviewersResponse.minStartDate).startOf('day')
    : dayjs().subtract(6, 'month').startOf('day');

  // When half-day is toggled, update end date to match start date
  const handleHalfDayToggle = (checked) => {
    setFieldValue('isHalfDay', checked);
    if (checked && values.startDate) {
      setFieldValue('endDate', values.startDate);
      if (!values.halfDayType) {
        setFieldValue('halfDayType', HALF_DAY_TYPES.FIRST_HALF);
      }
    } else {
      setFieldValue('halfDayType', '');
    }
  };

  // When start date changes and isHalfDay is true, update end date
  useEffect(() => {
    if (values.isHalfDay && values.startDate) {
      setFieldValue('endDate', values.startDate);
    }
  }, [values.startDate, values.isHalfDay, setFieldValue]);

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_LEAVE_FORM_STATE });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? 'Edit Leave Request' : 'Request Leave'}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Container maxWidth="sm">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Stack spacing={3}>
                  <FormControl fullWidth error={touched.leaveType && Boolean(errors.leaveType)}>
                    <InputLabel>Leave Type</InputLabel>
                    <Select
                      {...getFieldProps('leaveType')}
                      label="Leave Type"
                      value={values.leaveType}
                      onChange={(e) => {
                        const newLeaveType = e.target.value;
                        setFieldValue('leaveType', newLeaveType);
                        // Set default reason for sick/emergency leave type if reason is empty
                        if (newLeaveType === LEAVE_TYPES.SICK_EMERGENCY && !values.reason && !isEdit) {
                          setFieldValue('reason', 'Due to Sickness/Emergency');
                        }
                      }}
                    >
                      <MenuItem value={LEAVE_TYPES.SICK_EMERGENCY}>{LEAVE_TYPE_LABELS[LEAVE_TYPES.SICK_EMERGENCY]}</MenuItem>
                      <MenuItem value={LEAVE_TYPES.PERSONAL}>{LEAVE_TYPE_LABELS[LEAVE_TYPES.PERSONAL]}</MenuItem>
                    </Select>
                    {touched.leaveType && errors.leaveType && (
                      <FormHelperText>{errors.leaveType}</FormHelperText>
                    )}
                  </FormControl>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Start Date"
                      value={values.startDate ? dayjs(values.startDate) : null}
                      onChange={(newValue) => {
                        setFieldValue('startDate', newValue);
                        // If end date is before new start date, update end date
                        if (values.endDate && newValue && dayjs(values.endDate).isBefore(newValue)) {
                          setFieldValue('endDate', newValue);
                        }
                      }}
                      inputFormat="DD-MMM-YYYY"
                      minDate={startDateMin}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.startDate && Boolean(errors.startDate)}
                          helperText={touched.startDate && errors.startDate}
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={values.isHalfDay ? "Date (Half Day)" : "End Date"}
                      value={values.endDate ? dayjs(values.endDate) : null}
                      onChange={(newValue) => {
                        if (values.isHalfDay && values.startDate) {
                          // For half day, end date should match start date
                          setFieldValue('endDate', values.startDate);
                        } else {
                          setFieldValue('endDate', newValue);
                        }
                      }}
                      inputFormat="DD-MMM-YYYY"
                      minDate={values.startDate ? dayjs(values.startDate).startOf('day') : startDateMin}
                      disabled={values.isHalfDay}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.endDate && Boolean(errors.endDate)}
                          helperText={
                            touched.endDate 
                              ? errors.endDate 
                              : values.isHalfDay 
                                ? 'End date matches start date for half-day leave' 
                                : ''
                          }
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isHalfDay}
                        onChange={(e) => handleHalfDayToggle(e.target.checked)}
                      />
                    }
                    label="Half Day Leave"
                  />

                  {values.isHalfDay && (
                    <FormControl fullWidth error={touched.halfDayType && Boolean(errors.halfDayType)}>
                      <InputLabel>Half Day Type</InputLabel>
                      <Select
                        {...getFieldProps('halfDayType')}
                        label="Half Day Type"
                        value={values.halfDayType}
                        onChange={(e) => setFieldValue('halfDayType', e.target.value)}
                      >
                        <MenuItem value={HALF_DAY_TYPES.FIRST_HALF}>
                          {HALF_DAY_TYPE_LABELS[HALF_DAY_TYPES.FIRST_HALF]}
                        </MenuItem>
                        <MenuItem value={HALF_DAY_TYPES.SECOND_HALF}>
                          {HALF_DAY_TYPE_LABELS[HALF_DAY_TYPES.SECOND_HALF]}
                        </MenuItem>
                      </Select>
                      {touched.halfDayType && errors.halfDayType && (
                        <FormHelperText>{errors.halfDayType}</FormHelperText>
                      )}
                    </FormControl>
                  )}

                  <TextField
                    label="Reason"
                    {...getFieldProps('reason')}
                    inputProps={{ maxLength: 500 }}
                    error={touched.reason && Boolean(errors.reason)}
                    helperText={
                      isEdit && leaveData?.reason === SYSTEM_AUTO_LEAVE_REASON
                        ? 'Reason cannot be updated for system auto-approved leaves.'
                        : touched.reason
                          ? errors.reason
                          : `${values.reason?.length || 0}/500 characters`
                    }
                    multiline
                    rows={4}
                    fullWidth
                    required
                    disabled={isEdit && leaveData?.reason === SYSTEM_AUTO_LEAVE_REASON}
                  />

                  <FormControl fullWidth error={touched.reviewers && Boolean(errors.reviewers)} required>
                    <InputLabel>Select Reviewers (Managers/HR) *</InputLabel>
                    <Select
                      multiple
                      value={values.reviewers || []}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('reviewers', typeof value === 'string' ? value.split(',') : value);
                      }}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return '';
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((reviewerId) => {
                              const reviewer = reviewersList.find((r) => r.id === reviewerId);
                              return (
                                <Chip
                                  key={reviewerId}
                                  label={reviewer ? reviewer.userName : reviewerId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                      label="Select Reviewers (Managers/HR) *"
                    >
                      {reviewersList.map((reviewer) => (
                        <MenuItem key={reviewer.id} value={reviewer.id}>
                          {reviewer.userName} ({reviewer.userRoleId === 2 ? 'Manager' : reviewer.userRoleId === 6 ? 'HR' : 'Admin'})
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.reviewers && errors.reviewers && (
                      <FormHelperText>{errors.reviewers}</FormHelperText>
                    )}
                    {!errors.reviewers && (
                      <FormHelperText>
                        Select at least one reviewer (Manager). Admin and HR will automatically be included.
                      </FormHelperText>
                    )}
                  </FormControl>

                  <LoadingButton
                    fullWidth
                    size="large"
                    onClick={submitForm}
                    variant="contained"
                    loading={isSubmitting}
                  >
                    {isEdit ? 'Update Request' : 'Submit Request'}
                  </LoadingButton>
                </Stack>
              </Form>
            </FormikProvider>
          </Container>
        </Paper>
      </Box>
    </Modal>
  );
};

LeaveRequestForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  leaveData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default LeaveRequestForm;

